import React from "react";
import { Bell, Search, RefreshCw, Moon, Sun, LogOut } from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuthStore } from "../../lib/auth";
import { useThemeStore } from "../../lib/theme";
import { useNavigate } from "react-router-dom";

interface TopBarProps {
  pendingAlerts?: number;
  title?: string;
}

export function TopBar({ pendingAlerts = 0, title = "Dashboard" }: TopBarProps) {
  const user     = useAuthStore((s) => s.user);
  const logout   = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const isDark   = useThemeStore((s) => s.isDark);
  const toggle   = useThemeStore((s) => s.toggle);

  const now     = new Date();
  const timeStr = now.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("th-TH", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const initials  = user?.name?.slice(0, 2) ?? "??";
  const roleLabel = user?.role === "doctor" ? "แพทย์" : user?.role === "nurse" ? "พยาบาล" : user?.role ?? "";

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <header className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-6 py-3.5 flex items-center justify-between shrink-0 shadow-sm transition-colors duration-200">
      <div className="flex items-center gap-3">
        <img src="/tuendee_logo.png" alt="เตือนดี" className="w-8 h-8 rounded-lg object-cover shadow-sm" />
        <div>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h1>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-1.5">
            <Sun className="w-3 h-3 text-orange-400" />
            {dateStr} • {timeStr} น.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input placeholder="ค้นหาผู้ป่วย HN..."
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 w-56 transition-all" />
        </div>
        <button onClick={() => window.location.reload()}
          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>

        {/* Dark mode toggle */}
        <button onClick={toggle}
          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-amber-400 transition-colors"
          title={isDark ? "สลับเป็นโหมดสว่าง" : "สลับเป็นโหมดมืด"}>
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        <button className={cn("relative p-2 rounded-xl transition-colors",
          pendingAlerts > 0 ? "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400")}>
          <Bell className="w-5 h-5" />
          {pendingAlerts > 0 && (
            <>
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-ping" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </>
          )}
        </button>

        <div className="flex items-center gap-2.5 pl-2 border-l border-gray-100 dark:border-gray-800">
          <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-semibold flex items-center justify-center">
            {initials}
          </div>
          <div className="hidden md:block">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-none">{user?.name ?? "ผู้ใช้งาน"}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{roleLabel}</p>
          </div>
          <button onClick={handleLogout} title="ออกจากระบบ"
            className="ml-1 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
